import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import { useAuth } from "../context/AuthContext";
import { useGallery } from "../context/GalleryContext";
import { artImage } from "../utils/image";
import { tokens } from "../theme";
import { STATUS_LABEL } from "../types";
import type { Artwork, Collection } from "../types";
import SectionLabel from "../components/SectionLabel";
import ArtworkFormDialog from "../components/admin/ArtworkFormDialog";
import CollectionFormDialog from "../components/admin/CollectionFormDialog";
import ConfirmDialog from "../components/admin/ConfirmDialog";

const AddIcon = () => <Box component="span">+</Box>;
const EditIcon = () => <Box component="span">✎</Box>;
const DeleteIcon = () => <Box component="span">×</Box>;
const StarIcon = () => <Box component="span">★</Box>;
const MoreIcon = () => <Box component="span">⋮</Box>;

type Draft<T> = Omit<T, "id" | "createdAt">;

function Stat({ value, label }: { value: number | string; label: string }) {
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: tokens.display,
          fontSize: "2.4rem",
          lineHeight: 1,
          color: tokens.ink,
        }}
      >
        {value}
      </Typography>
      <Typography variant="overline" sx={{ color: tokens.taupe }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const gallery = useGallery();
  const { artworks, collections } = gallery;

  const [tab, setTab] = useState(0);

  const [artOpen, setArtOpen] = useState(false);
  const [artEditing, setArtEditing] = useState<Artwork | null>(null);

  const [colOpen, setColOpen] = useState(false);
  const [colEditing, setColEditing] = useState<Collection | null>(null);

  const [confirm, setConfirm] = useState<{
    kind: "art" | "col";
    id: string;
    name: string;
  } | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  const [rowMenu, setRowMenu] = useState<{
    el: HTMLElement;
    art: Artwork;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const collectionName = useMemo(() => {
    const map = new Map(collections.map((c) => [c.id, c.name]));
    return (id?: string | null) =>
      id ? (map.get(id) ?? "Unfiled") : "Unfiled";
  }, [collections]);

  const countFor = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of artworks) {
      if (a.collectionId)
        counts.set(a.collectionId, (counts.get(a.collectionId) ?? 0) + 1);
    }
    return (id: string) => counts.get(id) ?? 0;
  }, [artworks]);

  const availableCount = artworks.filter(
    (a) => a.status === "available",
  ).length;

  function saveArtwork(draft: Draft<Artwork>) {
    if (artEditing) {
      gallery.updateArtwork(artEditing.id, draft);
      setToast("Piece updated");
    } else {
      gallery.addArtwork(draft);
      setToast("Piece added");
    }
    setArtOpen(false);
    setArtEditing(null);
  }

  function saveCollection(draft: Draft<Collection>) {
    if (colEditing) {
      gallery.updateCollection(colEditing.id, draft);
      setToast("Collection updated");
    } else {
      gallery.addCollection(draft);
      setToast("Collection created");
    }
    setColOpen(false);
    setColEditing(null);
  }

  function doDelete() {
    if (!confirm) return;
    if (confirm.kind === "art") {
      gallery.removeArtwork(confirm.id);
      setToast("Piece removed");
    } else {
      gallery.removeCollection(confirm.id);
      setToast("Collection removed");
    }
    setConfirm(null);
  }

  return (
    <Box sx={{ bgcolor: tokens.bone, minHeight: "100vh", pb: 12 }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 8 } }}>
        <SectionLabel>Private studio</SectionLabel>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 3,
            mt: 1.5,
          }}
        >
          <Box>
            <Typography
              variant="h2"
              sx={{ fontSize: { xs: "2.6rem", md: "3.4rem" } }}
            >
              Manage the gallery
            </Typography>
            <Typography sx={{ mt: 1 }}>
              Signed in as {user?.name}. Changes save to this browser as you go.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 5, pb: 1 }}>
            <Stat value={artworks.length} label="Pieces" />
            <Stat value={availableCount} label="Available" />
            <Stat value={collections.length} label="Collections" />
          </Box>
        </Box>

        <Box sx={{ borderBottom: `1px solid ${tokens.line}`, mt: 4, mb: 4 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            TabIndicatorProps={{
              sx: { backgroundColor: tokens.brass, height: 2 },
            }}
          >
            <Tab
              label={`Works (${artworks.length})`}
              sx={{
                color: tokens.taupe,
                "&.Mui-selected": { color: tokens.ink },
              }}
            />
            <Tab
              label={`Collections (${collections.length})`}
              sx={{
                color: tokens.taupe,
                "&.Mui-selected": { color: tokens.ink },
              }}
            />
          </Tabs>
        </Box>

        {tab === 0 && (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setArtEditing(null);
                  setArtOpen(true);
                }}
              >
                Add a piece
              </Button>
            </Box>

            {artworks.length === 0 ? (
              <EmptyState text="No pieces yet. Add your first painting to begin." />
            ) : (
              <Box sx={{ display: "grid", gap: 2 }}>
                {artworks.map((a) => (
                  <Box
                    key={a.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "72px 1fr auto",
                        md: "96px 1fr 200px auto",
                      },
                      alignItems: "center",
                      gap: 2.5,
                      p: 1.5,
                      bgcolor: tokens.paper,
                      border: `1px solid ${tokens.line}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 72, md: 96 },
                        height: { xs: 72, md: 96 },
                        bgcolor: tokens.bone,
                        backgroundImage: `url(${artImage(a)})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontFamily: tokens.display,
                            fontSize: "1.4rem",
                            color: tokens.ink,
                            lineHeight: 1.2,
                          }}
                        >
                          {a.title || "Untitled"}
                        </Typography>
                        {a.featured && (
                          <Tooltip title="Featured on the home page">
                            <StarIcon
                              sx={{ fontSize: 18, color: tokens.brass }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: tokens.taupe, mt: 0.25 }}
                      >
                        {[a.medium, a.year].filter(Boolean).join(", ") ||
                          "No details yet"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: { xs: "none", md: "block" } }}>
                      <Chip
                        size="small"
                        label={STATUS_LABEL[a.status]}
                        sx={{
                          bgcolor:
                            a.status === "available"
                              ? "rgba(47,86,81,0.10)"
                              : tokens.bone,
                          color:
                            a.status === "available"
                              ? tokens.accent
                              : tokens.taupe,
                          border: `1px solid ${tokens.line}`,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: tokens.taupe, mt: 0.75 }}
                      >
                        {collectionName(a.collectionId)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Box sx={{ display: { xs: "none", sm: "flex" } }}>
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => {
                              setArtEditing(a);
                              setArtOpen(true);
                            }}
                          >
                            <EditIcon
                              sx={{ fontSize: 20, color: tokens.ink }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() =>
                              setConfirm({
                                kind: "art",
                                id: a.id,
                                name: a.title || "this piece",
                              })
                            }
                          >
                            <DeleteIcon
                              sx={{ fontSize: 20, color: tokens.taupe }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <IconButton
                        sx={{ display: { xs: "inline-flex", sm: "none" } }}
                        onClick={(e) =>
                          setRowMenu({ el: e.currentTarget, art: a })
                        }
                      >
                        <MoreIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setColEditing(null);
                  setColOpen(true);
                }}
              >
                New collection
              </Button>
            </Box>

            {collections.length === 0 ? (
              <EmptyState text="No collections yet. Group related pieces into a collection." />
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {collections.map((c) => {
                  const cover =
                    artworks.find((a) => a.id === c.coverArtId) ??
                    artworks.find((a) => a.collectionId === c.id);
                  return (
                    <Box
                      key={c.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "120px 1fr auto",
                        gap: 2.5,
                        p: 1.5,
                        bgcolor: tokens.paper,
                        border: `1px solid ${tokens.line}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          bgcolor: tokens.bone,
                          backgroundImage: cover
                            ? `url(${artImage(cover)})`
                            : "none",
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: tokens.display,
                            fontSize: "1.6rem",
                            color: tokens.ink,
                            lineHeight: 1.15,
                          }}
                        >
                          {c.name}
                        </Typography>
                        <Typography
                          variant="overline"
                          sx={{ color: tokens.brass }}
                        >
                          {countFor(c.id)} pieces
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: tokens.taupe,
                            mt: 0.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {c.description || "No description yet."}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => {
                              setColEditing(c);
                              setColOpen(true);
                            }}
                          >
                            <EditIcon
                              sx={{ fontSize: 20, color: tokens.ink }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() =>
                              setConfirm({
                                kind: "col",
                                id: c.id,
                                name: c.name,
                              })
                            }
                          >
                            <DeleteIcon
                              sx={{ fontSize: 20, color: tokens.taupe }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            mt: 10,
            pt: 4,
            borderTop: `1px solid ${tokens.line}`,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: tokens.display,
                fontSize: "1.4rem",
                color: tokens.ink,
              }}
            >
              Restore sample content
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.taupe }}>
              Replaces everything in this browser with the starter pieces and
              collections.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => setResetOpen(true)}>
            Reset gallery
          </Button>
        </Box>
      </Container>

      <ArtworkFormDialog
        open={artOpen}
        initial={artEditing}
        collections={collections}
        onClose={() => {
          setArtOpen(false);
          setArtEditing(null);
        }}
        onSave={saveArtwork}
      />

      <CollectionFormDialog
        open={colOpen}
        initial={colEditing}
        artworks={artworks}
        onClose={() => {
          setColOpen(false);
          setColEditing(null);
        }}
        onSave={saveCollection}
      />

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.kind === "col" ? "Delete collection?" : "Delete piece?"}
        message={
          confirm?.kind === "col"
            ? `"${confirm?.name}" will be removed. Pieces inside it stay in the gallery and become unfiled.`
            : `"${confirm?.name}" will be removed from the gallery. This cannot be undone.`
        }
        onConfirm={doDelete}
        onClose={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={resetOpen}
        title="Reset the gallery?"
        message="This replaces all current pieces and collections in this browser with the original sample content."
        confirmLabel="Reset"
        onConfirm={() => {
          gallery.resetToSeed();
          setToast("Gallery reset to sample content");
        }}
        onClose={() => setResetOpen(false)}
      />

      <Menu
        anchorEl={rowMenu?.el}
        open={!!rowMenu}
        onClose={() => setRowMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (rowMenu) {
              setArtEditing(rowMenu.art);
              setArtOpen(true);
            }
            setRowMenu(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenu)
              setConfirm({
                kind: "art",
                id: rowMenu.art.id,
                name: rowMenu.art.title || "this piece",
              });
            setRowMenu(null);
          }}
          sx={{ color: "#9b3a2f" }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Snackbar
        open={!!toast}
        autoHideDuration={2600}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setToast(null)}
          sx={{ bgcolor: tokens.accent }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <Box
      sx={{
        py: 10,
        textAlign: "center",
        border: `1px dashed ${tokens.line}`,
        bgcolor: tokens.paper,
      }}
    >
      <Typography
        sx={{
          fontFamily: tokens.display,
          fontSize: "1.6rem",
          color: tokens.taupe,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}
